// src/components/anlagen/AnlageDetailPage.jsx
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { showError } from "../../utils/toastUtils";
import { GoogleMap, Marker } from "@react-google-maps/api";
import {
  Box,
  Heading,
  Grid,
  Text,
  Button,
  Spinner,
  VStack,
  Icon,
} from "@chakra-ui/react";

// Chakra v3 color mode helper (adjust path if needed)
import { useColorModeValue } from "../ui/color-mode";

export default function AnlageDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [anlage, setAnlage] = useState(null);

  // Color tokens per mode
  const cardBg = useColorModeValue("white", "gray.800");
  const titleColor = useColorModeValue("blue.600", "blue.300");
  const borderColor = useColorModeValue("gray.200", "whiteAlpha.300");
  const muted = useColorModeValue("gray.500", "gray.400");

  useEffect(() => {
    const fetchAnlage = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/anlagen/${id}`,
          { headers: token ? { Authorization: `Bearer ${token}` } : {} }
        );
        setAnlage(res.data);
      } catch {
        showError("Fehler!", "Anlage konnte nicht geladen werden.");
        navigate("/");
      }
    };

    fetchAnlage();
  }, [id, navigate]);

  if (!anlage) {
    return (
      <Box p={6} textAlign="center">
        <Spinner size="lg" borderWidth="4px" color="blue.500" />
        <Text mt={4} color={muted}>
          Lade Daten...
        </Text>
      </Box>
    );
  }

  const center = {
    lat: parseFloat(anlage.latitude) || 52.52,
    lng: parseFloat(anlage.longitude) || 13.405,
  };

  return (
    <Box maxW="7xl" mx="auto" p={{ base: 4, md: 6 }}>
      <Heading as="h1" size="lg" color={titleColor} mb={6}>
        📄 Anlage Details – {anlage.anlagenName}
      </Heading>

      <Grid
        templateColumns={{ base: "1fr", md: "1fr 1fr" }}
        gap={6}
        alignItems="start"
      >
        {/* LEFT: Details */}
        <Box
          bg={cardBg}
          borderWidth="1px"
          borderColor={borderColor}
          boxShadow="md"
          borderRadius="xl"
          p={{ base: 4, md: 6 }}
        >
          <VStack align="start" spacing={3}>
            <Detail label="Projekt-Nr." value={anlage.projektNummer} />
            <Detail label="Name" value={anlage.anlagenName} />
            <Detail label="Ort" value={anlage.ort} />
            <Detail label="Größe (kWp)" value={anlage.anlagenGroesse} />
            <Detail label="Straße" value={anlage.strasse} />
            <Detail label="PLZ" value={anlage.plz} />
            <Detail label="Jahr der ersten Wartung" value={anlage.wartung?.jahrErsteWartung} />
            <Detail label="Art der Wartung" value={anlage.wartung?.artDerWartung} />
            <Detail label="Wartungsturnus" value={anlage.wartung?.wartungsturnus} />
            <Detail
              label="Latitude / Longitude"
              value={`${anlage.latitude ?? "-"} / ${anlage.longitude ?? "-"}`}
            />
            {/* --- Relations (counts) --- */}
            {anlage.kundenAnlagzuordnungen?.map((z, i) => (
              <React.Fragment key={z.id ?? i}>
                <Detail
                  label={`Kunde ${i + 1}`}
                  value={
                    z.kunde
                      ? `${z.kunde.firma ?? ""} ${z.kunde.vorname ?? ""} ${z.kunde.nachname ?? ""}`.trim()
                      : "-"
                  }
                />
                <Detail label="Vertragsbeginn" value={z.vertragsbeginn ?? "-"} />
                <Detail label="Leistung (kWp)" value={z.leistungKwp ?? "-"} />
              </React.Fragment>
            ))}
            {anlage.module?.map((m, i) => (
              <Detail
                key={i}
                label={`Modul ${i + 1}`}
                value={`${m.hersteller ?? "-"} ${m.typ ?? ""} (${m.leistungWp ?? "-"} Wp)`}
              />
            ))}
            {anlage.wechselrichter?.map((w, i) => (
              <Detail
                key={i}
                label={`Wechselrichter ${i + 1}`}
                value={`${w.hersteller ?? "-"} ${w.typ ?? ""} (${w.leistung ?? "-"} W)`}
              />
            ))}

            {anlage.stromspeicher?.map((s, i) => (
              <Detail
                key={i}
                label={`Stromspeicher ${i + 1}`}
                value={`${s.hersteller ?? "-"} ${s.typ ?? ""} (${s.leistung ?? "-"} kWh)`}
              />
            ))}
            {anlage.datenlogger?.map((d, i) => (
              <Detail
                key={i}
                label={`Datenlogger ${i + 1}`}
                value={`${d.hersteller ?? "-"} ${d.typ ?? ""}`}
              />
            ))}

            {/* --- One-to-One hardware --- */}
            <Detail
              label="Mobilfunk Router"
              value={
                anlage.mobilefunkRouter
                  ? `${anlage.mobilefunkRouter.hersteller ?? "-"} (${anlage.mobilefunkRouter.ipAdresse ?? "-"})`
                  : "-"
              }
            />
            <Detail
              label="Netzwerk Router"
              value={
                anlage.netzwerkRouterAnlage
                  ? `${anlage.netzwerkRouterAnlage.hersteller ?? "-"} (${anlage.netzwerkRouterAnlage.ipAdresse ?? "-"})`
                  : "-"
              }
            />
            <Detail
              label="Zähler"
              value={
                anlage.zaehlerAnlage
                  ? `${anlage.zaehlerAnlage.zaehlernummer} (Faktor: ${anlage.zaehlerAnlage.wandlerFaktor ?? "-"})`
                  : "-"
              }
            />
            {/* --- Dach / Montage --- */}
            <Detail
              label="Aufstellungsort"
              value={
                anlage.aufstellungsortAnlage
                  ? [
                    anlage.aufstellungsortAnlage.wohngebaeude && "Wohngebäude",
                    anlage.aufstellungsortAnlage.garage && "Garage",
                    anlage.aufstellungsortAnlage.landwirtschaftsgebaeude && "Landw. Gebäude",
                    anlage.aufstellungsortAnlage.gewerblicheHalle && "Gewerbl. Halle",
                    anlage.aufstellungsortAnlage.freiland && "Freiland",
                    anlage.aufstellungsortAnlage.andere && "Andere",
                  ]
                    .filter(Boolean)
                    .join(", ")
                  : "-"
              }
            />
            <Detail
              label="Dachangaben"
              value={
                anlage.angabenZumDachAnlage
                  ? [
                    anlage.angabenZumDachAnlage.satteldach && "Satteldach",
                    anlage.angabenZumDachAnlage.flachdach && "Flachdach",
                    anlage.angabenZumDachAnlage.pultdach && "Pultdach",
                    anlage.angabenZumDachAnlage.sheddach && "Sheddach",
                    anlage.angabenZumDachAnlage.andere && "Andere",
                  ]
                    .filter(Boolean)
                    .join(", ")
                  : "-"
              }
            />
            <Detail
              label="Dacheindeckung"
              value={
                anlage.dacheindeckungAnlage
                  ? [
                    anlage.dacheindeckungAnlage.ziegel && "Ziegel",
                    anlage.dacheindeckungAnlage.blech && "Blech",
                    anlage.dacheindeckungAnlage.bitumen && "Bitumen",
                    anlage.dacheindeckungAnlage.folie && "Folie",
                    anlage.dacheindeckungAnlage.andere && "Andere",
                  ]
                    .filter(Boolean)
                    .join(", ")
                  : "-"
              }
            />
            <Detail
              label="Schienensystem"
              value={
                anlage.schienensystemAnlage
                  ? [
                    anlage.schienensystemAnlage.einlagig && "einlagig",
                    anlage.schienensystemAnlage.zweilagig && "zweilagig",
                    anlage.schienensystemAnlage.aufgestaendert && "aufgestaendert",
                    anlage.schienensystemAnlage.andere && "andere",
                  ]
                    .filter(Boolean)
                    .join(", ")
                  : "-"
              }
            />
            <Detail
              label="Modulbefestigung"
              value={
                anlage.modulbefestigungAnlage
                  ? [
                    anlage.modulbefestigungAnlage.klemmen && "Klemmen",
                    anlage.modulbefestigungAnlage.einschubsystem && "Einschubsystem",
                    anlage.modulbefestigungAnlage.schraubeAnUK && "Schraube an UK",
                    anlage.modulbefestigungAnlage.andere && "Andere",
                  ]
                    .filter(Boolean)
                    .join(", ")
                  : "-"
              }
            />
            <Detail
              label="Befestigung"
              value={
                anlage.befestigungAnlage
                  ? [
                    anlage.befestigungAnlage.dachhacken && "Dachhacken",
                    anlage.befestigungAnlage.stockschrauben && "Stockschrauben",
                    anlage.befestigungAnlage.ballastierung && "Ballastierung",
                    anlage.befestigungAnlage.andere && "Andere",
                  ]
                    .filter(Boolean)
                    .join(", ")
                  : "-"
              }
            />
            <Detail
              label="Beschwerung"
              value={
                anlage.beschwerungAnlage
                  ? [
                    anlage.beschwerungAnlage.wannen && "Wannen",
                    anlage.beschwerungAnlage.steine && "Steine",
                    anlage.beschwerungAnlage.kies && "Kies",
                    anlage.beschwerungAnlage.andere && "Andere",
                  ]
                    .filter(Boolean)
                    .join(", ")
                  : "-"
              }
            />

            {/* --- Ausrichtung --- */}
            <Detail
              label="Ausrichtung"
              value={
                anlage.ausrichtungNeigungModule
                  ? [
                    anlage.ausrichtungNeigungModule.sued && "Süd",
                    anlage.ausrichtungNeigungModule.ost && "Ost",
                    anlage.ausrichtungNeigungModule.west && "West",
                    anlage.ausrichtungNeigungModule.ostWest && "Ost–West",
                    anlage.ausrichtungNeigungModule.nord && "Nord",
                    anlage.ausrichtungNeigungModule.suedOst && "Süd-Ost",
                    anlage.ausrichtungNeigungModule.suedWest && "Süd-West",
                    anlage.ausrichtungNeigungModule.nordOst && "Nord-Ost",
                    anlage.ausrichtungNeigungModule.nordWest && "Nord-West",
                    anlage.ausrichtungNeigungModule.andere && "Andere",
                  ]
                    .filter(Boolean)
                    .join(", ")
                  : "-"
              }
            />
            <Detail
              label="Neigung"
              value={
                anlage.neigungModuleAnlage?.neigung != null
                  ? `${anlage.neigungModuleAnlage.neigung}°`
                  : "-"
              }
            />
          </VStack>
        </Box>

        {/* RIGHT: Map */}
        <Box
          bg={cardBg}
          borderWidth="1px"
          borderColor={borderColor}
          boxShadow="md"
          borderRadius="xl"
          overflow="hidden"
        >
          <GoogleMap
            mapContainerStyle={{ width: "100%", height: "400px" }}
            center={center}
            zoom={12}
          >
            <Marker position={center} />
          </GoogleMap>
        </Box>
      </Grid>

      {/* Back button (v3: no leftIcon prop) */}
      <Button
        onClick={() => navigate(-1)}
        mt={6}
        colorScheme="blue"
        display="inline-flex"
        gap="2"
      >
        <Icon as={() => <span>🔙</span>} />
        Zurück zur Liste
      </Button>
    </Box>
  );
}

function Detail({ label, value }) {
  return (
    <Text>
      <Text as="span" fontWeight="semibold">
        {label}:
      </Text>{" "}
      {value || "-"}
    </Text>
  );
}